import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { ClientStageGroup, ListFaClientsParams, VisibilityScope, ClientsOnboardingScope } from "../types";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import { useCompanyId } from "@/context/CompanyContext";
import { clientsKeys, type ClientsPageKeyInput } from "./keys";

/** Filters managed by the clients list UI. */
export interface ClientsFilters {
  /** Free-text search (matched server-side on display_name). */
  q: string;
  visibility: VisibilityScope;
  /** Pulse journey stage group (paraplanner pills). */
  stageGroup?: ClientStageGroup;
  /** Active onboarding mission only vs full stage caseload. */
  onboardingScope?: ClientsOnboardingScope;
}

export const DEFAULT_CLIENTS_FILTERS: ClientsFilters = {
  q: "",
  visibility: "all",
};

/**
 * Server cap we request. The backend allows up to 500 — if the response is
 * full we surface "500+" in the count so the user knows results are truncated.
 */
export const CLIENTS_FETCH_LIMIT = 500;

const SEARCH_DEBOUNCE_MS = 200;

/** Serialise list filters into a stable TanStack Query cache key fragment. */
export function buildClientsFiltersKey(filters: ClientsFilters): string {
  return JSON.stringify({
    q: filters.q.trim().toLowerCase(),
    v: filters.visibility,
    sg: filters.stageGroup ?? "",
    os: filters.onboardingScope ?? "all",
  });
}

/** Build the full clients-page cache key input from list filters. */
export function buildClientsPageKeyInput(
  filters: ClientsFilters,
  companyId?: string,
): ClientsPageKeyInput {
  return {
    filtersKey: buildClientsFiltersKey(filters),
    visibility: filters.visibility,
    ...(companyId ? { companyId } : {}),
  };
}

function filtersToApiParams(filters: ClientsFilters): ListFaClientsParams {
  const apiParams: ListFaClientsParams = { limit: CLIENTS_FETCH_LIMIT };
  if (filters.q.trim()) apiParams.q = filters.q.trim();
  if (filters.visibility !== "all") apiParams.visibility = filters.visibility;
  if (filters.stageGroup) apiParams.stage_group = filters.stageGroup;
  if (filters.onboardingScope && filters.onboardingScope !== "all") {
    apiParams.onboarding_scope = filters.onboardingScope;
  }
  return apiParams;
}

export interface UseClientsPageOptions {
  filters: ClientsFilters;
  enabled?: boolean;
}

/**
 * Cached enriched clients list for the knowledge-centre page.
 * Pauses fetching when the OS window is minimised, unfocused, or in a background workspace.
 */
export function useClientsPage({
  filters,
  enabled: enabledOption = true,
}: UseClientsPageOptions) {
  const isWindowLive = useWindowLive();
  const companyId = useCompanyId();
  const queryEnabled = enabledOption && isWindowLive;
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedFilters(filters),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [filters]);

  const keyInput = useMemo(
    () => buildClientsPageKeyInput(debouncedFilters, companyId ?? undefined),
    [companyId, debouncedFilters],
  );
  const searching = debouncedFilters.q.trim() !== "";

  const query = useQuery({
    queryKey: clientsKeys.page(keyInput),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    placeholderData: (prev) => (searching ? undefined : prev),
    queryFn: () => AgenticV2API.listClients(filtersToApiParams(debouncedFilters)),
  });

  const clients = query.data ?? [];
  const loading = query.isLoading && clients.length === 0;
  const refetching = query.isFetching && !loading;
  const hasMore = clients.length >= CLIENTS_FETCH_LIMIT;

  return {
    clients,
    loading,
    refetching,
    error: query.error,
    hasMore,
    refresh: () => void query.refetch(),
    keyInput,
  };
}

/** QueryClient accessor for clients-page cache invalidation. */
export function useClientsPageQueryClient() {
  return useQueryClient();
}
